import { NetworkStatus, useReactiveVar } from "@apollo/client";
import React, { useCallback, useMemo } from "react";

import RecommendationView from "./PlayerDetailRightView";

import SkeletonList from "@/components/organisms/SkeletonList";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { useAuth } from "@/features/auth";
import { useRecommendationTypes } from "@/features/recommendations";
import { useRecommendationsListPlayerRecommendationsQuery } from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import {
  NoteActionsheetState,
  noteActionsheetStateVar,
} from "@/stores/noteActionsheetState";
import { recommendationsCategorySelectedVar } from "@/stores/recommendationsCategorySelection";

/**
 *
 */
interface IRightColumnContainerProps {
  type: "note" | "recommendation";
  playerId?: string;
}
/**
 * Container component for player detail right column with notes and recommendations
 * @param root0 - Component props
 * @param root0.type - Column content type (note or recommendation)
 * @param root0.playerId - Unique identifier of the player for context-specific recommendations
 */
const RightColumnContainer = ({
  type,
  playerId,
}: IRightColumnContainerProps) => {
  const auth = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { defaultRecommendationType, recommendationTypes } =
    useRecommendationTypes();
  const noteState = useReactiveVar(noteActionsheetStateVar);
  const recommendationsCategorySelected =
    useReactiveVar(recommendationsCategorySelectedVar) ||
    defaultRecommendationType;
  const isSmallScreen = useMobileScreen();

  const handleSelectRecommendationsCategory = useCallback((type: string) => {
    recommendationsCategorySelectedVar(type);
  }, []);

  const handleChangeStatusEditNote = useCallback(() => {
    noteActionsheetStateVar(
      NoteActionsheetState.Form === noteState
        ? NoteActionsheetState.List
        : NoteActionsheetState.Form
    );
  }, [noteState]);

  const { data, refetch, loading, networkStatus } =
    useRecommendationsListPlayerRecommendationsQuery({
      variables: {
        recommendationType: recommendationsCategorySelected,
      },
      skip: !auth?.data?.accessToken,
      notifyOnNetworkStatusChange: true,
    });

  const dataRecommendationsCategorySelection = useMemo(
    () => data?.listPlayerRecommendations ?? [],
    [data?.listPlayerRecommendations]
  );

  const handleRefresh = useCallback(
    () =>
      refetch({
        recommendationType: recommendationsCategorySelected,
      }),
    [recommendationsCategorySelected, refetch]
  );

  const renderListEmptyComponent = useCallback(
    () =>
      loading ? (
        <SkeletonList type="player" />
      ) : (
        <Stack className="flex-1 items-center justify-center gap-3">
          <Text variant="display-sm" className="text-center">
            No Players
          </Text>
        </Stack>
      ),
    [loading]
  );

  return (
    <RecommendationView
      type={type}
      playerId={playerId}
      recommendationTypes={recommendationTypes}
      recommendationsCategorySelected={recommendationsCategorySelected}
      handleSelectRecommendationsCategory={handleSelectRecommendationsCategory}
      data={dataRecommendationsCategorySelection}
      refreshing={networkStatus === NetworkStatus.refetch}
      onRefresh={handleRefresh}
      noteState={noteState}
      isSmallScreen={isSmallScreen}
      onChangeStatusNote={handleChangeStatusEditNote}
      renderListEmptyComponent={renderListEmptyComponent}
      isDark={isDark}
    />
  );
};

export default RightColumnContainer;
